import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { comics, readerPages } from "@/mocks/comics";
import ReaderCatalog from "@/pages/read/components/ReaderCatalog";
import EmptyState from "@/components/feature/EmptyState";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";

type FontSize = "small" | "medium" | "large";
type ReaderTheme = "day" | "night";

const WIDTH_MAP: Record<FontSize, number> = {
  small: 620,
  medium: 820,
  large: 1040,
};

export default function Read() {
  const { comicId, chapterId } = useParams<{ comicId: string; chapterId: string }>();
  const [fontSize, setFontSize] = useState<FontSize>("medium");
  const [theme, setTheme] = useState<ReaderTheme>("day");
  const [catalogOpen, setCatalogOpen] = useState(false);

  const comic = comics.find((c) => c.id === comicId);
  const chapterIndex = comic
    ? comic.chapters.findIndex((c) => c.id === chapterId)
    : -1;
  const chapter = comic && chapterIndex >= 0 ? comic.chapters[chapterIndex] : undefined;

  useDocumentTitle(
    comic && chapter ? `${comic.title} ${chapter.title} - 老司机漫画` : "漫画阅读 - 老司机漫画",
  );

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [chapterId]);

  const images = useMemo(() => {
    if (chapterIndex < 0) return [];
    const offset = chapterIndex % readerPages.length;
    return [...readerPages.slice(offset), ...readerPages.slice(0, offset)];
  }, [chapterIndex]);

  if (!comic || !chapter) {
    return (
      <main className="min-h-screen flex items-center justify-center px-4 bg-background-100">
        <EmptyState
          title="章节不存在"
          desc="你要阅读的章节未找到，返回详情页重新选择章节吧。"
        />
      </main>
    );
  }

  const isNight = theme === "night";
  const prev = chapterIndex > 0 ? comic.chapters[chapterIndex - 1] : undefined;
  const next =
    chapterIndex < comic.chapters.length - 1 ? comic.chapters[chapterIndex + 1] : undefined;

  const shellCls = isNight ? "bg-foreground-950 text-background-100" : "bg-background-100 text-foreground-950";
  const barCls = isNight
    ? "bg-foreground-900/95 border-foreground-800"
    : "bg-background-50/95 border-background-200";
  const ctrlBtn = isNight
    ? "hover:bg-foreground-800 text-background-200"
    : "hover:bg-background-100 text-foreground-700";
  const sizeBtns: FontSize[] = ["small", "medium", "large"];
  const sizeLabels: Record<FontSize, string> = { small: "小", medium: "中", large: "大" };

  return (
    <div className={`min-h-screen flex flex-col ${shellCls}`}>
      <header className={`sticky top-0 z-30 border-b backdrop-blur ${barCls}`}>
        <div className="w-full px-3 md:px-6 h-14 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <Link
              to={`/detail/${comic.id}`}
              className={`w-9 h-9 rounded-md flex items-center justify-center shrink-0 cursor-pointer ${ctrlBtn}`}
              aria-label="返回详情"
            >
              <i className="ri-arrow-left-line text-xl"></i>
            </Link>
            <div className="min-w-0">
              <p className="text-sm font-medium truncate">{comic.title}</p>
              <p className={`text-xs truncate ${isNight ? "text-background-400" : "text-foreground-500"}`}>
                {chapter.title}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-1 md:gap-2">
            <div
              className={`hidden sm:flex items-center gap-0.5 p-0.5 rounded-full ${
                isNight ? "bg-foreground-800" : "bg-background-100"
              }`}
            >
              {sizeBtns.map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setFontSize(s)}
                  className={`w-8 h-8 rounded-full text-xs font-medium transition-colors cursor-pointer ${
                    fontSize === s
                      ? "bg-primary-500 text-background-50"
                      : isNight
                        ? "text-background-300 hover:text-background-100"
                        : "text-foreground-600 hover:text-foreground-950"
                  }`}
                >
                  {sizeLabels[s]}
                </button>
              ))}
            </div>

            <button
              type="button"
              onClick={() => setTheme(isNight ? "day" : "night")}
              aria-label="切换阅读背景"
              className={`w-9 h-9 rounded-md flex items-center justify-center cursor-pointer ${ctrlBtn}`}
            >
              <i className={isNight ? "ri-sun-line text-xl" : "ri-moon-line text-xl"}></i>
            </button>

            <button
              type="button"
              onClick={() => setCatalogOpen(true)}
              aria-label="打开目录"
              className={`h-9 px-3 rounded-md flex items-center gap-1 text-sm cursor-pointer ${ctrlBtn}`}
            >
              <i className="ri-list-unordered text-lg"></i>
              <span className="hidden md:inline">目录</span>
            </button>
          </div>
        </div>
      </header>

      <main className="flex-1 w-full px-2 md:px-4 py-4 md:py-6">
        <div className="mx-auto flex flex-col gap-3 transition-all" style={{ maxWidth: WIDTH_MAP[fontSize] }}>
          {images.map((src, idx) => (
            <div
              key={`${chapter.id}-${idx}`}
              className={`w-full rounded-md overflow-hidden ${isNight ? "bg-foreground-900" : "bg-background-200"}`}
            >
              <img
                src={src}
                alt={`${comic.title} ${chapter.title} 第 ${idx + 1} 页`}
                title={`${comic.title} ${chapter.title} 漫画页面`}
                className="w-full h-auto block"
              />
            </div>
          ))}
        </div>

        <p className={`text-center text-sm mt-6 mb-2 ${isNight ? "text-background-400" : "text-foreground-500"}`}>
          —— {chapter.title} 完 ——
        </p>

        <div className="mx-auto flex items-center justify-center gap-3 mt-4 mb-6 flex-wrap" style={{ maxWidth: 820 }}>
          {prev ? (
            <Link
              to={`/read/${comic.id}/${prev.id}`}
              className="inline-flex items-center gap-2 h-11 px-6 rounded-md bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-arrow-left-line"></i>
              上一章
            </Link>
          ) : (
            <span className="inline-flex items-center gap-2 h-11 px-6 rounded-md bg-background-300/40 text-foreground-500 font-medium text-sm cursor-not-allowed whitespace-nowrap">
              <i className="ri-arrow-left-line"></i>
              已是第一章
            </span>
          )}

          <button
            type="button"
            onClick={() => setCatalogOpen(true)}
            className={`inline-flex items-center gap-2 h-11 px-6 rounded-md font-medium text-sm transition-colors cursor-pointer whitespace-nowrap ${
              isNight
                ? "bg-foreground-800 text-background-100 hover:bg-foreground-700"
                : "bg-background-50 border border-background-300 text-foreground-800 hover:border-primary-400 hover:text-primary-600"
            }`}
          >
            <i className="ri-list-unordered"></i>
            章节目录
          </button>

          {next ? (
            <Link
              to={`/read/${comic.id}/${next.id}`}
              className="inline-flex items-center gap-2 h-11 px-6 rounded-md bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              下一章
              <i className="ri-arrow-right-line"></i>
            </Link>
          ) : (
            <span className="inline-flex items-center gap-2 h-11 px-6 rounded-md bg-background-300/40 text-foreground-500 font-medium text-sm cursor-not-allowed whitespace-nowrap">
              已是最后一章
              <i className="ri-arrow-right-line"></i>
            </span>
          )}
        </div>

        <div className="text-center">
          <Link
            to={`/detail/${comic.id}`}
            className={`inline-flex items-center gap-2 text-sm transition-colors cursor-pointer ${isNight ? "text-background-300 hover:text-primary-400" : "text-foreground-600 hover:text-primary-600"}`}
          >
            <i className="ri-information-line"></i>
            返回漫画详情
          </Link>
        </div>
      </main>

      <ReaderCatalog
        open={catalogOpen}
        onClose={() => setCatalogOpen(false)}
        comicId={comic.id}
        comicTitle={comic.title}
        chapters={comic.chapters}
        currentId={chapter.id}
      />
    </div>
  );
}